import * as React from 'react';
import { z } from 'zod';
import { CheckCircle2, FileText, X } from 'lucide-react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { WorkspaceField, WorkspaceInput } from '@/components/ui/workspace/field';
import { ApiError } from '@/lib/api/client';
import { readBytes } from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Quote } from '../kb-bits';
import { DropBox } from '../sources/source-forms';
import { formatDate } from '../kb-logic';

/**
 * The page a vendor sees from an upload link (T-0207; E13-F05 §3.2,
 * prototype `uploadPage()`). No account: the link's token stays in the URL
 * fragment and is sent only in the `X-Caliber-Link` header, never in a path
 * or a query. It shows only the request, its expiry, the limits and this
 * link's own files (rule 20). Works on a phone.
 */

export const VendorUploadSessionSchema = z.object({
  purpose: z.literal('vendor_upload'),
  firm_name: z.string(),
  vendor_name: z.string(),
  request: z.string(),
  expires_at: z.string(),
  limits: z.object({
    max_bytes: z.number(),
    max_files: z.number(),
    types: z.array(z.string()),
  }),
  files: z.array(z.object({ file_name: z.string(), received_at: z.string(), status: z.string() })),
});
export type VendorUploadSession = z.infer<typeof VendorUploadSessionSchema>;

const LINK_HEADER = 'X-Caliber-Link';

/**
 * A call that carries the link and nothing else: no session, no sign-in
 * redirect on a refused link (the app's client would send a vendor to the
 * firm's login page). Errors keep the server's message.
 */
export async function linkFetch<T>(
  path: string,
  token: string,
  schema: z.ZodType<T>,
  init: { method?: 'GET' | 'POST'; body?: BodyInit; headers?: Record<string, string> } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    body: init.body,
    headers: { Accept: 'application/json', [LINK_HEADER]: token, ...(init.headers ?? {}) },
    cache: 'no-store',
  });
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  if (!res.ok) {
    const e = (
      payload as {
        error?: { code?: string; message?: string; details?: Record<string, unknown> };
      } | null
    )?.error;
    throw new ApiError(
      res.status,
      e?.code ?? 'internal',
      e?.message ?? 'Something went wrong. Try again.',
      '',
      e?.details,
    );
  }
  return schema.parse(payload);
}

type Pick = {
  key: string;
  file: File;
  problem: string | null;
  state: 'ready' | 'sending' | 'sent' | 'refused';
};

function extensionOf(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return m ? m[1]!.toLowerCase() : '';
}

function sizeText(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const TYPE_WORDS = 'PDF, Word (.docx), Excel (.xlsx, .xls), text or CSV';

export function UploadFrame({ children, until }: { children: React.ReactNode; until?: string }) {
  return (
    <div className="flex min-h-screen justify-center bg-paper-50 px-4 pb-16 pt-10">
      <div className="flex w-[720px] max-w-full flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <img src="/avero-logo.png" alt="Avero Advisors" className="h-9" />
          <span className="text-meta text-ink-500">Secure upload link · no account needed</span>
        </div>
        {children}
        <div className="text-center text-meta text-ink-500">
          {until
            ? `This link was sent by Avero Advisors for one request and works until ${formatDate(until)}.`
            : 'Links from Avero Advisors are for one request each.'}
        </div>
      </div>
    </div>
  );
}

function Card({ children, center = false }: { children: React.ReactNode; center?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-large border border-ink-100 bg-paper-0 shadow-modal',
        center
          ? 'flex flex-col items-center gap-2.5 px-6 py-12 text-center'
          : 'px-5 py-6 sm:px-8 sm:py-7',
      )}
    >
      {children}
    </div>
  );
}

export function NoLongerActive() {
  return (
    <UploadFrame>
      <Card center>
        <h1 className="m-0 text-h1 font-semibold text-ink-900">This link is no longer active</h1>
        <p className="m-0 text-body text-ink-700">Ask the person who sent it for a new one.</p>
      </Card>
    </UploadFrame>
  );
}

export function VendorUploadPage({
  token,
  session,
}: {
  token: string;
  session: VendorUploadSession;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [picks, setPicks] = React.useState<Pick[]>([]);
  const [name, setName] = React.useState('');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const [done, setDone] = React.useState<string[] | null>(null);
  const [gone, setGone] = React.useState(false);
  const { limits } = session;
  const already = session.files.length;
  const room = Math.max(0, limits.max_files - already);

  const add = (files: File[]) => {
    setError(null);
    setPicks((prev) => {
      const next = [...prev];
      for (const file of files) {
        if (next.some((p) => p.file.name === file.name && p.file.size === file.size)) continue;
        const ext = extensionOf(file.name);
        const problem = !limits.types.includes(ext)
          ? `This type can’t be sent. Send a ${TYPE_WORDS} file.`
          : file.size > limits.max_bytes
            ? `Over ${sizeText(limits.max_bytes)}. Send a smaller file.`
            : null;
        next.push({
          key: `${file.name}:${file.size}:${next.length}`,
          file,
          problem,
          state: problem ? 'refused' : 'ready',
        });
      }
      return next;
    });
  };

  const ready = picks.filter((p) => p.state === 'ready');
  const tooMany = ready.length > room;

  const send = async () => {
    if (!name.trim()) {
      setError('Type your name, so Avero knows who sent the files.');
      return;
    }
    setSending(true);
    setError(null);
    const sent: string[] = [];
    for (const p of ready) {
      setPicks((prev) => prev.map((x) => (x.key === p.key ? { ...x, state: 'sending' } : x)));
      try {
        await linkFetch(
          '/api/v1/secure-links/vendor-upload',
          token,
          z.object({ file_name: z.string(), received_at: z.string() }),
          {
            method: 'POST',
            body: await readBytes(p.file),
            headers: {
              'Content-Type': 'application/octet-stream',
              'X-File-Name': encodeURIComponent(p.file.name),
              'X-Sender-Name': encodeURIComponent(name.trim()),
              ...(note.trim() ? { 'X-Sender-Note': encodeURIComponent(note.trim()) } : {}),
            },
          },
        );
        sent.push(p.file.name);
        setPicks((prev) => prev.map((x) => (x.key === p.key ? { ...x, state: 'sent' } : x)));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setGone(true);
          setSending(false);
          return;
        }
        const message =
          err instanceof ApiError ? err.message : 'The file could not be sent. Try again.';
        setPicks((prev) =>
          prev.map((x) => (x.key === p.key ? { ...x, state: 'refused', problem: message } : x)),
        );
      }
    }
    setSending(false);
    if (sent.length > 0 && sent.length === ready.length) setDone(sent);
  };

  if (gone) return <NoLongerActive />;

  if (done) {
    return (
      <UploadFrame until={session.expires_at}>
        <Card center>
          <CheckCircle2 className="h-7 w-7 text-posture-healthy" aria-hidden="true" />
          <h1 className="m-0 text-h1 font-semibold text-ink-900">Received</h1>
          <p className="m-0 text-body text-ink-700">
            {done.length === 1 ? `${done[0]} was sent.` : `${done.length} files were sent.`} Avero
            Advisors will review them before using them. You can close this page.
          </p>
        </Card>
      </UploadFrame>
    );
  }

  return (
    <UploadFrame until={session.expires_at}>
      <Card>
        <div className="text-meta text-ink-500">For {session.vendor_name}</div>
        <h1 className="m-0 mt-1 text-h1 font-semibold text-ink-900">
          Documents for {session.firm_name}
        </h1>
        <div className="mt-[18px] rounded-card border border-ink-100 bg-paper-50 px-4 py-3.5">
          <div className="text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
            What we’d like
          </div>
          <Quote text={session.request} />
        </div>

        {room === 0 ? (
          <div className="mt-4 rounded-card border border-ink-100 bg-paper-50 px-4 py-3 text-body-sm text-ink-700">
            This link has taken all the files it can ({limits.max_files}). Ask the person who sent
            it for another link.
          </div>
        ) : (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              aria-label="Choose files"
              accept={limits.types.map((t) => `.${t}`).join(',')}
              onChange={(e) => {
                add(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
            <DropBox
              multiple
              onPick={() => inputRef.current?.click()}
              onDropMany={add}
              label="Drop files here"
              or="or choose files"
              hint={`${TYPE_WORDS}. Up to ${sizeText(limits.max_bytes)} each, ${room} more ${room === 1 ? 'file' : 'files'} on this link.`}
            />
          </>
        )}

        {picks.length > 0 ? (
          <ul className="m-0 mt-3 list-none border-t border-ink-100 p-0">
            {picks.map((p) => (
              <li key={p.key} className="border-b border-ink-100 px-0.5 py-2">
                <div className="flex items-center gap-2.5 text-body-sm">
                  <FileText className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
                  <span className="min-w-0 flex-1 break-words">
                    <span className="font-medium text-ink-900">{p.file.name}</span>{' '}
                    <span className="text-meta text-ink-500">{sizeText(p.file.size)}</span>
                  </span>
                  {p.state === 'sending' ? (
                    <span className="text-meta text-ink-500">Sending…</span>
                  ) : p.state === 'sent' ? (
                    <span className="text-meta text-posture-healthy">Sent</span>
                  ) : (
                    <button
                      type="button"
                      aria-label={`Remove ${p.file.name}`}
                      disabled={sending}
                      onClick={() => setPicks((prev) => prev.filter((x) => x.key !== p.key))}
                      className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {p.problem ? (
                  <div role="alert" className="ml-[26px] mt-1 text-meta text-posture-critical">
                    {p.problem}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {room > 0 ? (
          <div className="mt-4 grid grid-cols-1 gap-3">
            <WorkspaceField label="Your name">
              {(id) => (
                <WorkspaceInput
                  id={id}
                  value={name}
                  maxLength={120}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              )}
            </WorkspaceField>
            <WorkspaceField label="A note for Avero (optional)">
              {(id) => (
                <textarea
                  id={id}
                  rows={2}
                  maxLength={1000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="block w-full rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600"
                />
              )}
            </WorkspaceField>
          </div>
        ) : null}

        {tooMany ? (
          <div role="alert" className="mt-2 text-meta text-posture-critical">
            This link takes {room} more {room === 1 ? 'file' : 'files'}. Remove some before sending.
          </div>
        ) : null}
        {error ? (
          <div role="alert" className="mt-2 text-meta text-posture-critical">
            {error}
          </div>
        ) : null}

        <div className="mt-[18px] flex flex-wrap items-center gap-3">
          <span className="min-w-0 flex-1 text-meta text-ink-500">
            This link works until {formatDate(session.expires_at)}.
          </span>
          {room > 0 ? (
            <WorkspaceButton
              variant="primary"
              size="md"
              disabled={ready.length === 0 || tooMany || sending}
              onClick={() => void send()}
            >
              {sending ? 'Sending…' : `Send to ${session.firm_name}`}
            </WorkspaceButton>
          ) : null}
        </div>

        {already > 0 ? (
          <div className="mt-5">
            <div className="text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
              Already sent through this link
            </div>
            <ul className="m-0 mt-1.5 list-none border-t border-ink-100 p-0">
              {session.files.map((f, k) => (
                <li
                  key={`${f.file_name}:${k}`}
                  className="flex items-center gap-2.5 border-b border-ink-100 py-2 text-body-sm"
                >
                  <FileText className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
                  <span className="min-w-0 flex-1 break-words text-ink-900">{f.file_name}</span>
                  <span className="flex-none text-meta text-ink-500">
                    {formatDate(f.received_at)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>
    </UploadFrame>
  );
}
